import React, { useMemo, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import DataAnalysisFilters from '../../components/DataAnalysisFilters/DataAnalysisFilters'
import { ChartExportWrapper, StackedBarChart, ToggleButton } from 'components'
import { getTabByKey, prettyAttr, TAB_KEYS } from '../../utils/consts'
import {
  selectChartData,
  selectChartStatus,
  selectWheatColorByCodeMap,
} from 'store/selectors/dataAnalysisSelectors'
import { applyAndFetchChart } from 'store/slices/dataAnalysisSlice'
import { toDistributionChartData } from '../../transforms'
import EmptyState from '../../components/EmptyState'
import { TDistributionData } from 'types/models/DataAnalysis'
import * as sxStyles from '../tabs.styles'
import { DataTabsLayout, TrendChartStatus } from '../../layouts'
import normalizeDataset from '../../utils/normalizeDataset'

export const StorageDistribution = () => {
  const dispatch = useDispatch()
  const rawData = useSelector(selectChartData(TAB_KEYS.STORAGE_DISTRIBUTION))
  const status = useSelector(selectChartStatus(TAB_KEYS.STORAGE_DISTRIBUTION))
  const colorByCode = useSelector(selectWheatColorByCodeMap)
  const currentTab = getTabByKey(TAB_KEYS.STORAGE_DISTRIBUTION, 'storage')

  const [mode, setMode] = useState<'count' | 'percentage'>('percentage')

  useEffect(() => {
    dispatch(applyAndFetchChart({ key: TAB_KEYS.STORAGE_DISTRIBUTION }))
  }, [dispatch])

  const data = useMemo(
    () => normalizeDataset<TDistributionData>(rawData as any),
    [rawData]
  )
  const { rows, series } = useMemo(
    () => toDistributionChartData(data, mode, colorByCode),
    [data, mode, colorByCode]
  )

  const hasAnyData = rows.length > 0

  return (
    <DataTabsLayout
      sidebar={
        <DataAnalysisFilters
          filtersList={currentTab?.filters ?? []}
          chartKey={TAB_KEYS.STORAGE_DISTRIBUTION}
        />
      }
      tabKey={TAB_KEYS.STORAGE_DISTRIBUTION}
      onApply={() =>
        dispatch(applyAndFetchChart({ key: TAB_KEYS.STORAGE_DISTRIBUTION }))
      }
    >
      <TrendChartStatus status={status} title={TAB_KEYS.STORAGE_DISTRIBUTION}>
        {!hasAnyData ? (
          <EmptyState />
        ) : (
          <Box display="flex" justifyContent="center" width="100%">
            <Card
              data-exportable-chart="true"
              variant="outlined"
              sx={{
                ...sxStyles.parametersComparisonCard,
                width: '100%',
              }}
            >
              <CardHeader
                title={prettyAttr('Storage Distribution')}
                titleTypographyProps={{ fontSize: 24, fontWeight: 800 }}
              />
              <CardContent
                sx={{
                  ...sxStyles.parametersComparisonCardContent,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <ChartExportWrapper title="Storage Distribution">
                    <StackedBarChart
                      data={rows}
                      categoryKey="binLabel"
                      series={series}
                      percentage={mode === 'percentage'}
                      minLabelValue={5}
                    />
                  </ChartExportWrapper>
                </Box>
              </CardContent>
            </Card>
          </Box>
        )}
      </TrendChartStatus>
    </DataTabsLayout>
  )
}
